<script setup lang="ts">
// biome-ignore lint/correctness/noUnusedImports: used via tag <UiPopup> in <template>
import { UiPopup } from '@/ui-kit';

interface Props {
  variant: 'confirmation' | 'printing-locked';
  groupCount?: number;
  individualCount?: number;
  groupPrice?: string;
  individualPrice?: string;
  totalSum?: string;
}

withDefaults(defineProps<Props>(), {
  groupCount: 0,
  individualCount: 0,
  groupPrice: '',
  individualPrice: '',
  totalSum: '',
});

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<{
  close: [];
  confirm: [];
}>();
</script>

<template>
  <UiPopup
    v-if="variant === 'confirmation'"
    :classes="{ scrollable: 'Portraits__confirmation-scrollable' }"
    style="--kiosk-popup-scrollable-padding: var(--portrait-order-dialog-scrollable-padding, 3rem)"
    @close="emit('close')"
  >
    <div class="Portraits__confirmation-content">
      <h2 class="Portraits__confirmation-caption">
        {{ $t('Portraits.popupTitle') }}
      </h2>

      <div class="Portraits__confirmation-breakdown">
        <div
          v-if="groupCount > 0"
          class="Portraits__confirmation-breakdown-line"
        >
          {{
            $t('Portraits.popupGroupSelected', {
              count: groupCount,
              price: groupPrice,
            })
          }}
        </div>
        <div
          v-if="individualCount > 0"
          class="Portraits__confirmation-breakdown-line"
        >
          {{
            $t('Portraits.popupIndividualSelected', {
              count: individualCount,
              price: individualPrice,
            })
          }}
        </div>
        <div class="Portraits__confirmation-breakdown-total">
          {{ $t('Portraits.popupTotalSum', { sum: totalSum }) }}
        </div>
      </div>

      <div class="Portraits__confirmation-actions">
        <button
          class="Portraits__confirmation-button kiosk-button-inline"
          data-testid="portraits-confirm-no"
          @click="emit('close')"
        >
          {{ $t('Portraits.popupButtonNo') }}
        </button>
        <button
          class="Portraits__confirmation-button kiosk-button"
          data-testid="portraits-confirm-yes"
          @click="emit('confirm')"
        >
          {{ $t('Portraits.popupButtonYes') }}
        </button>
      </div>
    </div>
  </UiPopup>

  <UiPopup v-else size="sm" @close="emit('close')">
    <h2 data-testid="portraits-printing-locked">
      {{ $t('Portraits.printingLockedPopupTitle') }}
    </h2>
    <p>{{ $t('Portraits.printingLockedPopupCaption') }}</p>
  </UiPopup>
</template>

<style lang="scss" scoped>
.Portraits__confirmation-content {
  display: flex;
  flex-direction: column;
  text-transform: var(--Kiosk--font_capitalize, uppercase);
}

.Portraits__confirmation-caption {
  margin-bottom: var(--portrait-order-dialog-caption-margin-bottom, 1rem);
  font-family: var(--Kiosk--font_headers_url);
  font-size: var(--portrait-order-dialog-caption-font-size, 200%);
  line-height: 1.1;
  text-align: center;
}

:global(
  .kiosk--capitalize-text .Portraits__confirmation-caption
) {
  text-transform: var(--Kiosk--font_capitalize, uppercase);
}

.Portraits__confirmation-breakdown {
  margin-bottom: var(--portrait-order-dialog-breakdown-margin-bottom, 1rem);
  font-family: var(--Kiosk--font_headers_url);
  text-align: center;
}

.Portraits__confirmation-breakdown-line {
  margin-bottom: var(--portrait-order-dialog-line-margin-bottom, 0.5rem);
  font-size: var(--portrait-order-dialog-line-font-size, 150%);
  line-height: 1.4;
}

.Portraits__confirmation-breakdown-total {
  margin-top: var(--portrait-order-dialog-total-margin-top, 1rem);
  font-size: var(--portrait-order-dialog-total-font-size, 170%);
  font-weight: 600;
  line-height: 1.4;
}

.Portraits__confirmation-actions {
  display: flex;
  flex-direction: var(--portrait-order-dialog-actions-direction, row);
  justify-content: center;
  margin-top: var(--portrait-order-dialog-actions-margin-top, 1.4rem);
}

.Portraits__confirmation-button {
  width: var(--portrait-order-dialog-button-width, min-content);
  margin-top: var(--portrait-order-dialog-button-margin-top, 0);
  margin-right: var(--portrait-order-dialog-button-margin-right, 3.3rem);

  &:last-child {
    margin-top: 0;
    margin-right: 0;
  }

  &.kiosk-button {
    height: var(
      --portrait-order-dialog-block-button-height,
      var(--kiosk-button-height)
    );
    font-size: var(
      --portrait-order-dialog-block-button-font-size,
      var(--kiosk-button-font-size)
    );
  }

  &.kiosk-button-inline {
    height: var(
      --portrait-order-dialog-inline-button-height,
      var(--kiosk-button-inline-height)
    );
    font-size: var(
      --portrait-order-dialog-inline-button-font-size,
      var(--kiosk-button-inline-font-size)
    );
  }
}
</style>
